import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { FloatingEntry } from "~/store/eventsTimerSlice";
import type { HistoryFilterRange } from "~mobile/events/helpers/myTaskAssignmentsAdapter";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import isRecord from "~/helpers/isRecord";

export const EVENTS_TIME_SOURCE_KIND = "eventsTime";

export const EVENTS_HISTORY_SOURCE_KIND = "eventsHistory";

export const EVENTS_HISTORY_EDITOR_KEY = "history";

export const EVENTS_TASK_TARGET_KIND = "eventsTask";

export const EVENTS_BASE_PATH = "/events";

export const EVENTS_TASKS_RAIL_ID = "tasks";

const EVENTS_TASK_ROUTE_PATTERN = /^\/events\/tasks\/([^/?#]+)$/;

export interface EventsCreatedTask {
	isQueued: boolean;
	jobNumber: null | string;
	taskAssignmentId: string;
	taskEventTypeName: string;
}

export interface EventsTaskPick {
	createdTask: EventsCreatedTask | null;
	taskId: null | string;
}

export const NO_TASK_PICK: EventsTaskPick = { createdTask: null, taskId: null };

export type EventsTimeSurface = "prompt" | "queue";

export interface EventsTimeSourceCheckpoint {
	entry: FloatingEntry;
	pick: EventsTaskPick;
	surface: EventsTimeSurface;
}

const isFloatingEntry = (value: unknown): value is FloatingEntry =>
	isRecord(value) &&
	typeof value.id === "string" &&
	typeof value.duration === "number" &&
	Array.isArray(value.allocations);

const isCreatedTask = (value: unknown): value is EventsCreatedTask =>
	isRecord(value) &&
	typeof value.isQueued === "boolean" &&
	(value.jobNumber === null || typeof value.jobNumber === "string") &&
	typeof value.taskAssignmentId === "string" &&
	typeof value.taskEventTypeName === "string";

const isTaskPick = (value: unknown): value is EventsTaskPick =>
	isRecord(value) &&
	(value.createdTask === null || isCreatedTask(value.createdTask)) &&
	(value.taskId === null || typeof value.taskId === "string");

export const isEventsTimeSourceCheckpoint = (value: unknown): value is EventsTimeSourceCheckpoint =>
	isRecord(value) &&
	isFloatingEntry(value.entry) &&
	isTaskPick(value.pick) &&
	(value.surface === "prompt" || value.surface === "queue");

export interface EventsHistorySourceCheckpoint {
	range: HistoryFilterRange;
	taskEventName: string;
}

const isHistoryFilterRange = (value: unknown): value is HistoryFilterRange => value === "day" || value === "week";

export const isEventsHistorySourceCheckpoint = (value: unknown): value is EventsHistorySourceCheckpoint =>
	isRecord(value) && isHistoryFilterRange(value.range) && typeof value.taskEventName === "string";

export const buildEventsTaskTarget = (taskAssignmentId: string): ContinuationTarget => ({
	targetId: taskAssignmentId,
	targetKind: EVENTS_TASK_TARGET_KIND
});

export const matchEventsTaskReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = EVENTS_TASK_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		return buildEventsTaskTarget(decodeURIComponent(match[1]));
	} catch {
		return null;
	}
};

export const eventsTaskReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${EVENTS_BASE_PATH}/tasks/${encodeURIComponent(targetId)}`,
	matchesRoute: matchEventsTaskReceiverRoute
};

const buildEventsTimeEditorKey = (floatingTimeEntryId: string): string =>
	`${EVENTS_TASKS_RAIL_ID}:${floatingTimeEntryId}`;

export default buildEventsTimeEditorKey;
